/* eslint-disable @scandipwa/scandipwa-guidelines/derived-class-names,no-undef */
/**
 * @category    ScandiPWA
 * @package     ScandiPWA_CjPublicis
 * @author      Ricards Nedosvitnijs <info@scandiweb.com>
 * @copyright   Copyright (c) 2022 Scandiweb, Ltd (https://scandiweb.com)
 */

import { fetchQuery } from 'Util/Request/Query';

import OrderDataQuery from '../query/Order.query';

export class CheckoutSuccessPlugin {
    sendOrderDataToCj(orderID) {
        if (!orderID) {
            return;
        }

        fetchQuery(OrderDataQuery.getOrderDataQuery(orderID)).then(
            (result) => {
                if (result) {
                    const {
                        getOrderData: {
                            actionTrackerId,
                            amount,
                            cjPlugin,
                            coupon,
                            currency,
                            customerCountry,
                            customerStatus,
                            discount,
                            discounts,
                            emailHash,
                            enterpriseId,
                            lineItems,
                            orderId,
                            prices,
                            quantities,
                            taxAmount,
                            userId
                        }
                    } = result;

                    if (!window.cj) {
                        window.cj = {};
                    }

                    cj.order = {};
                    cj.sitePage = {};

                    cj.order.enterpriseId = enterpriseId;
                    cj.order.orderId = orderId;
                    cj.order.actionTrackerId = actionTrackerId;
                    cj.order.currency = currency;
                    cj.order.amount = amount;
                    cj.order.taxAmount = taxAmount;
                    cj.order.discount = discount < 0 ? discount * (-1) : discount;
                    cj.order.coupon = coupon;
                    cj.order.customerCountry = customerCountry;

                    cj.order.items = [];
                    cj.order.customerstatus = customerStatus;
                    cj.order.pageType = 'conversionConfirmation';
                    cj.order.userId = userId;
                    cj.order.emailHash = emailHash;

                    // eslint-disable-next-line fp/no-loops,fp/no-let
                    for (let i = 0; i < lineItems.length; i++) {
                        const anItemObj = {};
                        anItemObj.unitPrice = prices[i];
                        anItemObj.itemId = lineItems[i].replace(/ /g, '_');
                        anItemObj.quantity = quantities[i];
                        if (discounts[i] != null && discounts[i] !== 0) {
                            anItemObj.discount = discounts[i];
                        }
                        cj.order.items.push(anItemObj);
                    }

                    cj.order.cjPlugin = cjPlugin;

                    cj.sitePage.enterpriseId = enterpriseId;
                    cj.sitePage.pageType = 'conversionConfirmation';
                    cj.sitePage.userId = userId;
                    cj.sitePage.emailHash = emailHash;
                    cj.sitePage.cartSubtotal = amount;

                    cj.sitePage.items = [];
                    // eslint-disable-next-line fp/no-loops,fp/no-let
                    for (let i = 0; i < lineItems.length; i++) {
                        const anItemObj = {};
                        anItemObj.unitPrice = prices[i];
                        anItemObj.itemId = lineItems[i].replace(/ /g, '_');
                        anItemObj.quantity = quantities[i];
                        if (discounts[i] != null && discounts[i] !== 0) {
                            anItemObj.discount = discounts[i];
                        }
                        cj.sitePage.items.push(anItemObj);
                    }

                    if (cj.order.customerstatus === '') {
                        // eslint-disable-next-line fp/no-delete
                        delete cj.order.customerstatus;
                    }
                    if (cj.order.coupon === '') {
                        // eslint-disable-next-line fp/no-delete
                        delete cj.order.coupon;
                    }
                    if (cj.order.userId === '') {
                        // eslint-disable-next-line fp/no-delete
                        delete cj.order.userId;
                    }
                    if (cj.sitePage.userId === '') {
                        // eslint-disable-next-line fp/no-delete
                        delete cj.sitePage.userId;
                    }
                    if (cj.order.customerCountry === '') {
                        // eslint-disable-next-line fp/no-delete
                        delete cj.order.customerCountry;
                    }

                    if (this.getCookie() && cjApi) {
                        cjApi.sendOrder(
                            this.prepareCjOrder(cj.order)
                        );
                    }
                }
            }
        );
    }

    getCookie() {
        const value = `; ${ document.cookie }`;
        const parts = value.split('; cje=');
        if (parts.length === 2) {
            return parts.pop().split(';').shift();
        }

        return null;
    }

    prepareCjOrder(cj) {
        const order = {
            currency: cj.currency,
            amount: cj.amount,
            taxAmount: cj.taxAmount,
            discount: cj.discount,
            customerCountry: cj.customerCountry,
            customerstatus: cj.customerstatus,
            pageType: cj.pageType,
            emailHash: cj.emailHash,
            cjPlugin: cj.cjPlugin,
            oid: cj.orderId,
            type: cj.actionTrackerId,
            cid: cj.enterpriseId,
            userId: cj.userId ? cj.userId : '',
            coupon: cj.coupon ? cj.coupon : ''
        };

        const cjItems = cj.items;

        // eslint-disable-next-line fp/no-let,fp/no-loops
        for (let i = 0; i < cjItems.length; i++) {
            order[`item${i + 1}`] = cjItems[i].itemId;
            order[`qty${i + 1}`] = cjItems[i].quantity;
            order[`amt${i + 1}`] = cjItems[i].unitPrice;

            if (cjItems[i].discount != null && cjItems[i].discount !== 0) {
                order[`dcnt${i + 1}`] = cjItems[i].discount;
            }
        }

        if (order.coupon === '') {
            // eslint-disable-next-line fp/no-delete
            delete order.coupon;
        }
        if (order.userId === '') {
            // eslint-disable-next-line fp/no-delete
            delete order.userId;
        }

        return order;
    }

    beforeComponentDidMount = (args, callback = () => {}, instance) => {
        const { orderID } = instance.props;

        this.sendOrderDataToCj(orderID);

        return callback.apply(instance, args);
    };

    beforeRender = (args, callback = () => {}, instance) => {
        const { orderId } = instance.props;

        if (!window.cj || (window.cj.order && window.cj.order.orderId !== orderId)) {
            this.sendOrderDataToCj(orderId);
        }

        return callback.apply(instance, args);
    };
}

const {
    beforeComponentDidMount,
    beforeRender
} = new CheckoutSuccessPlugin();

export const config = {
    'Component/CheckoutSuccess/Component': {
        'member-function': {
            componentDidMount: beforeComponentDidMount
        }
    },
    'Checkoutcom/Route/CheckoutComThreeDs/Component': {
        'member-function': {
            render: beforeRender
        }
    }
};

export default config;
